import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Headphones, Check, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from '@/hooks/use-toast';
import { usePreferences } from '@/context/PreferencesContext';

export const LANGUAGES = [
    { code: 'en-IN', name: 'English (India)' },
    { code: 'hi-IN', name: 'Hindi' },
    { code: 'ar-SA', name: 'Arabic' },
    { code: 'bn-IN', name: 'Bengali' },
    { code: 'gu-IN', name: 'Gujarati' },
    { code: 'kn-IN', name: 'Kannada' },
    { code: 'ml-IN', name: 'Malayalam' },
    { code: 'mr-IN', name: 'Marathi' },
    { code: 'ne-NP', name: 'Nepali' },
    { code: 'or-IN', name: 'Odia' },
    { code: 'pa-IN', name: 'Punjabi' },
    { code: 'ta-IN', name: 'Tamil' },
    { code: 'te-IN', name: 'Telugu' },
    { code: 'ur-IN', name: 'Urdu' }
];

const LanguageSelectionModal = ({ open, onClose, onSaveSuccess, isForceSelection = false }) => {
    const { theme, preferences, updatePreferences } = usePreferences();
    const [selectedLanguages, setSelectedLanguages] = useState([]);
    const [saving, setSaving] = useState(false);
    const isDark = theme !== 'light';

    useEffect(() => {
        if (open && preferences?.preferred_languages) {
            setSelectedLanguages(preferences.preferred_languages);
        }
    }, [open, JSON.stringify(preferences?.preferred_languages)]);

    const handleToggleLanguage = (code) => {
        setSelectedLanguages((prev) => {
            const isSelected = prev.includes(code);
            if (isSelected) {
                return prev.filter(c => c !== code);
            } else {
                if (prev.length >= 3) {
                    toast({
                        title: 'Selection Limit Reached',
                        description: 'You can select up to 3 languages.',
                        variant: 'warning'
                    });
                    return prev;
                }
                return [...prev, code];
            }
        });
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await updatePreferences({
                preferred_languages: selectedLanguages
            });
            toast({
                title: 'Preferences Saved',
                description: 'Your voice tutor preferred languages have been updated.',
            });
            onSaveSuccess?.(selectedLanguages);
            onClose();
        } catch (error) {
            console.error('Failed to save language preferences:', error);
            toast({
                title: 'Error Saving',
                description: 'Failed to update your language preferences. Please try again.',
                variant: 'destructive'
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(val) => { if (!val) onClose(); }}>
            <DialogContent 
                onPointerDownOutside={(e) => { if (isForceSelection) e.preventDefault(); }}
                onEscapeKeyDown={(e) => { if (isForceSelection) e.preventDefault(); }}
                className={cn(
                    "sm:max-w-xl border shadow-2xl p-0 overflow-hidden rounded-3xl flex flex-col",
                    "max-sm:fixed max-sm:inset-x-4 max-sm:top-[calc(3.5rem+env(safe-area-inset-top,0px)+0.75rem)] max-sm:bottom-[calc(4rem+env(safe-area-inset-bottom,0px)+0.75rem)] max-sm:translate-x-0 max-sm:translate-y-0 max-sm:max-h-none sm:max-h-[90vh]",
                    "bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))] text-foreground shadow-2xl"
                )}
            >
                <DialogHeader className="px-6 py-4 max-sm:py-2 text-center border-b border-[hsl(var(--card-border))] flex flex-col items-center justify-center relative bg-[hsl(var(--main-bg))]">
                    <div className="h-14 w-14 rounded-full flex items-center justify-center mb-2 max-sm:mb-0 shadow-md bg-emerald-500/15 text-[hsl(var(--green-bright))] dark:text-emerald-400">
                        <Headphones className="w-7 h-7 animate-pulse" />
                    </div>
                    <DialogTitle className="text-xl font-bold tracking-tight text-foreground">
                        Select Tutor Languages
                    </DialogTitle>
                    <DialogDescription className="text-xs mt-1 text-muted-foreground">
                        Choose up to 3 languages you'd like to speak in.
                    </DialogDescription>
                </DialogHeader>

                <div className="p-6 space-y-5 overflow-y-auto min-h-0 flex-1">
                    <div className="grid grid-cols-2 gap-2 pr-1">
                        {LANGUAGES.map((lang) => {
                            const isSelected = selectedLanguages.includes(lang.code);
                            const isPrimary = selectedLanguages[0] === lang.code;
                            return (
                                <div
                                    key={lang.code}
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => handleToggleLanguage(lang.code)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            handleToggleLanguage(lang.code);
                                        }
                                    }}
                                    className={cn(
                                        "flex items-center justify-between py-1.5 px-3 rounded-xl border text-left transition-all duration-200 select-none cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-emerald-500",
                                        isPrimary
                                            ? "bg-emerald-500/20 border-emerald-500 text-emerald-600 dark:text-emerald-300 shadow-md shadow-emerald-500/10"
                                            : isSelected
                                                ? "bg-teal-500/15 border-teal-500 text-teal-700 dark:text-teal-300 shadow-sm"
                                                : "bg-[hsl(var(--main-bg))] border-[hsl(var(--card-border))] text-muted-foreground hover:text-foreground hover:border-emerald-500/40"
                                    )}
                                >
                                    <div className="flex flex-col gap-1.5 w-full">
                                        {/* Top row: name + tick aligned */}
                                        <div className="flex items-center justify-between gap-1.5">
                                            <span className="text-sm font-semibold truncate flex-1 min-w-0">{lang.name}</span>
                                            {isSelected ? (
                                                <div className={cn(
                                                    "flex h-5 w-5 items-center justify-center rounded-full text-white shrink-0 shadow-sm",
                                                    isPrimary ? "bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))]" : "bg-teal-500"
                                                )}>
                                                    <Check className="h-3 w-3 stroke-[2.5]" />
                                                </div>
                                            ) : (
                                                <div className="h-5 w-5 border border-[hsl(var(--card-border))] rounded-full shrink-0" />
                                            )}
                                        </div>
                                        {/* Set Primary button below, only when selected */}
                                        {isSelected && (
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setSelectedLanguages(prev => {
                                                        const filtered = prev.filter(c => c !== lang.code);
                                                        return [lang.code, ...filtered];
                                                    });
                                                }}
                                                className={cn(
                                                    "self-start px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider transition-all border",
                                                    isPrimary
                                                        ? "bg-emerald-500/20 border-emerald-500/40 text-emerald-600 dark:text-emerald-400"
                                                        : "bg-[hsl(var(--main-bg))] border-[hsl(var(--card-border))] text-muted-foreground hover:text-foreground hover:border-emerald-500/30"
                                                )}
                                            >
                                                {isPrimary ? 'Primary ✓' : 'Set Primary'}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <DialogFooter className="p-4 border-t border-[hsl(var(--card-border))] flex flex-col sm:flex-row gap-3 bg-[hsl(var(--main-bg))]">
                    {!isForceSelection && (
                        <Button
                            type="button"
                            variant="ghost"
                            onClick={onClose}
                            className="w-full sm:w-auto text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--card-bg))]"
                            disabled={saving}
                        >
                            Cancel
                        </Button>
                    )}
                    <Button
                        type="button"
                        onClick={handleSave}
                        disabled={saving}
                        className="w-full sm:w-auto ml-auto bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))] hover:opacity-90 text-white font-semibold px-8 shadow-lg shadow-emerald-600/20 border-0"
                    >
                        {saving ? (
                            <>
                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                Saving...
                            </>
                        ) : (
                            'Save Preferences'
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default LanguageSelectionModal;
